import React, { useState, useEffect, useCallback, useRef } from 'react'; // Import necessary React hooks
import { auth, db } from '../firebaseConfig'; // Import authentication and database configurations
import { collection, addDoc, getDocs, query, where, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore'; // Import Firestore functions
import { onAuthStateChanged } from "firebase/auth"; // Import authentication state listener
import Note from './Note'; // Import Note component
import SimilarNotes from './SimilarNotes'; // Import SimilarNotes component
import AppBar from './AppBar'; // Import AppBar component
import { getEmbedding } from '../utils/embeddings'; // Import getEmbedding function

// Move this outside your component function to prevent it from being recreated on each render
const notesCollectionRef = collection(db, "notes_v2");

function Notes() {
  // State management for the component
  const [currentUser, setCurrentUser] = useState(null); // State to hold the current user
  const [notes, setNotes] = useState([]); // Stores all notes for the current user
  const [isSimilarNotesLoading, setIsSimilarNotesLoading] = useState(false); // State to manage loading state for similar notes
  const [similarNotes, setSimilarNotes] = useState([]); // State to hold similar notes
  const newNoteRef = useRef(null); // Ref to track the newly created note
  const [isSimilarityPanelVisible, setIsSimilarityPanelVisible] = useState(false); // State to manage visibility of similarity panel
  const [activeNoteId, setActiveNoteId] = useState(null); // State to hold the active note ID
  const scrollContainerRef = useRef(null); // Ref for the scroll container
  const [groupedNotes, setGroupedNotes] = useState({}); // Add grouped notes state

  // Add auth state listener to track user authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user); // Update current user state
    });
    return () => unsubscribe(); // Cleanup listener on unmount
  }, []);

  // Update data loading effect to group notes by date
  useEffect(() => {
    if (!currentUser) return;
    
    const q = query(
      notesCollectionRef, 
      where("uid", "==", currentUser.uid),
      where("type", "==", "note")
    );
    
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      console.log(`[${new Date().toISOString()}] Notes listener triggered - ${querySnapshot.docs.length} docs`);
      const notesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Format createdAt date
        let formattedCreatedAt = data.createdAt;
        try {
          if (data.createdAt?.toDate) {
            formattedCreatedAt = data.createdAt.toDate().toISOString(); // Convert Firestore timestamp to ISO string
          } else if (!data.createdAt) {
            formattedCreatedAt = new Date().toISOString(); // Default to current date if no createdAt
          }
        } catch (error) {
          formattedCreatedAt = new Date().toISOString(); // Fallback in case of error
        }

        return {
          id: doc.id, // Note ID
          ...data, // Spread other note data
          text: data.content, // Map content to text
          createdAt: formattedCreatedAt // Formatted createdAt date
        };
      });

      // Sort notes by date in descending order
      const sortedNotes = notesData.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      // Group notes by date
      const grouped = {};
      sortedNotes.forEach(note => {
        const dateKey = new Date(note.createdAt).toDateString();
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(note);
      });
      
      setGroupedNotes(grouped);
      setNotes(sortedNotes);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Scroll to the bottom of the notes container when a new note is added
  useEffect(() => {
    if (scrollContainerRef.current && newNoteRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight; // Scroll to the bottom
      newNoteRef.current = null; // Reset after scrolling
    }
  }, [notes]);

  // Function to create a new note
  const handleCreateNote = async (text = "") => {
    try {
      const now = new Date().toISOString(); // Get current timestamp
      
      // Add new note document to Firestore
      const docRef = await addDoc(notesCollectionRef, { 
        content: text,
        uid: currentUser.uid, // Associate note with current user
        createdAt: now,
        updatedAt: now,
        type: 'note', // Specify note type
        embedding: [] // Initialize empty embedding
      });
      
      newNoteRef.current = docRef.id; // Store reference to the new note

      // Calculate embedding and update storage if text is provided
      if (text) {
        const embedding = await getEmbedding(text); // Get embedding for the note text
        await Promise.all([
          updateDoc(doc(db, "notes_v2", docRef.id), { embedding }), // Update note document with embedding
          updatePineconeVector(docRef.id, embedding, { // Update Pinecone vector
            content: text,
            userId: currentUser.uid,
            timestamp: now
          })
        ]);
      }
    } catch (error) {
      console.error("Error creating note:", error); // Log error if note creation fails
    }
  };

  // Function to update Pinecone vector for a note
  const updatePineconeVector = async (id, vector, metadata) => {
    try {
      const response = await fetch('/api/hello', {
        method: 'POST', // Use POST method to update vector
        headers: {
          'Content-Type': 'application/json', // Set content type to JSON
        },
        body: JSON.stringify({ id, vector, metadata }) // Send ID, vector, and metadata in request body
      });

      if (!response.ok) {
        const error = await response.json(); // Parse error response
        console.error('Error updating vector in Pinecone:', error); // Log error if update fails
      }
    } catch (error) {
      console.error('Error calling Pinecone API:', error); // Log error if API call fails
    }
  };

  // Format date for display - simplified version without Today/Yesterday
  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Render notes with date separators appearing after the notes for each day
  const renderGroupedNotes = () => {
    const result = [];
    
    // Get dates in descending order
    const dates = Object.keys(groupedNotes).sort(
      (a, b) => new Date(b) - new Date(a)
    );
    
    dates.forEach(dateKey => {
      // First add the notes for this date
      groupedNotes[dateKey].forEach(note => {
        result.push(
          <Note 
            key={note.id}
            note={note}
            onNoteChange={() => {}}
            onFindSimilar={() => {
              setIsSimilarityPanelVisible(true);
              setActiveNoteId(note.id);
              setIsSimilarNotesLoading(true);
            }}
            autoFocus={note.id === newNoteRef.current}
          />
        );
      });
      
      // Then add date separator after the notes
      result.push(
        <div key={`date-${dateKey}`} className="border-t border-gray-200 px-6 py-2">
          <div className="text-xl font-bold">
            {formatDateHeader(dateKey)}
          </div>
        </div>
      );
    });
    
    return result;
  };

  return (
    <div className="flex-col h-[100dvh] overflow-hidden"> {/* Main container for the notes component */}
      <div className='flex bg-gray-50 h-[100dvh]'> {/* Flex container for layout */}
        {/* Main notes panel */}
        <div className="flex-1 min-w-[320px] max-w-[440px] border-r border-200 bg-white flex flex-col pb-[var(--app-bar-height,120px)]">
          <div 
            ref={scrollContainerRef} // Reference for scrolling
            className="flex-1 overflow-y-auto flex flex-col-reverse" // Flex container for notes with reverse order
          >
            {renderGroupedNotes()}
          </div>
        </div>

        {/* Conditionally render SimilarNotes */}
        {isSimilarityPanelVisible && (
          <SimilarNotes
            activeNoteId={activeNoteId} // Pass active note ID
            similarNotes={similarNotes} // Pass similar notes
            setSimilarNotes={setSimilarNotes} // Pass setter for similar notes
            onClose={() => setIsSimilarityPanelVisible(false)} // Close handler for similarity panel
          />
        )}
      </div>
      <AppBar 
        className="fixed bottom-0 left-0 right-0" // Fixed position for AppBar
        onSubmit={(text) => handleCreateNote(text)} // Submit handler for creating notes
      />
    </div>
  );
}

export default Notes; // Export Notes component
